import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Nav,
  Alert,
} from "react-bootstrap";
import {
  FaMobileAlt,
  FaArrowLeft,
  FaCheckCircle,
  FaEnvelope,
  FaLock,
  FaUserPlus,
  FaClock,
} from "react-icons/fa";

import { auth, db } from "../firebase";
import logo from "../assets/TW logo img.jpeg";

function CustomerLogin() {
  const navigate = useNavigate();

  const [loginMode, setLoginMode] = useState("email");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const normalizeEmail = (value) => {
    return value.trim().toLowerCase();
  };

  const saveCustomerSession = (customer) => {
    localStorage.setItem("customerEmail", customer.email || "");
    localStorage.setItem("customerPhone", customer.phone || "");
    localStorage.setItem("customerName", customer.name || "");
  };

  const emailLogin = async () => {
    try {
      if (!email || !password) {
        alert("Please enter email and password.");
        return;
      }

      setLoading(true);

      const cleanEmail = normalizeEmail(email);

      await signInWithEmailAndPassword(auth, cleanEmail, password);

      const customerRef = doc(db, "customers", cleanEmail);
      const customerSnap = await getDoc(customerRef);

      if (!customerSnap.exists()) {
        await signOut(auth);
        localStorage.clear();

        alert(
          "Login successful, but this email is not added as a Trading Wealth customer. Please contact admin."
        );

        setLoading(false);
        return;
      }

      const customerData = {
        id: customerSnap.id,
        ...customerSnap.data(),
      };

      saveCustomerSession(customerData);

      setLoading(false);
      navigate("/customer-dashboard");
    } catch (error) {
      console.error(error);

      alert("Wrong Email or Password.");

      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    try {
      if (!email) {
        alert("Please enter your registered email first.");
        return;
      }

      await sendPasswordResetEmail(auth, normalizeEmail(email));

      alert("Password reset link sent to your email.");
    } catch (error) {
      console.error(error);
      alert("Unable to send password reset email.");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a, #14532d)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card
              className="shadow-lg border-0"
              style={{ borderRadius: "22px" }}
            >
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <div
                    style={{
                      width: "85px",
                      height: "85px",
                      borderRadius: "50%",
                      border: "3px solid #22c55e",
                      overflow: "hidden",
                      margin: "0 auto 15px",
                    }}
                  >
                    <img
                      src={logo}
                      alt="Trading Wealth"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>

                  <h2 className="fw-bold mb-1">Customer Login</h2>

                  <p className="text-muted mb-0">
                    Login with your registered email and password.
                  </p>
                </div>

                <Nav
                  variant="pills"
                  className="mb-4 d-flex gap-2"
                  activeKey={loginMode}
                  onSelect={(selectedKey) => setLoginMode(selectedKey)}
                >
                  <Nav.Item className="flex-fill">
                    <Nav.Link eventKey="email" className="text-center">
                      <FaEnvelope className="me-2" />
                      Email Login
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item className="flex-fill">
                    <Nav.Link eventKey="mobile" className="text-center">
                      <FaMobileAlt className="me-2" />
                      Mobile Login
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                {loginMode === "email" && (
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FaEnvelope className="me-2 text-success" />
                        Email Address
                      </Form.Label>

                      <Form.Control
                        type="email"
                        placeholder="Enter registered email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <FaLock className="me-2 text-primary" />
                        Password
                      </Form.Label>

                      <Form.Control
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </Form.Group>

                    <Button
                      variant="success"
                      className="w-100 mb-3"
                      onClick={emailLogin}
                      disabled={loading}
                    >
                      <FaCheckCircle className="me-2" />
                      {loading ? "Logging in..." : "Login"}
                    </Button>

                    <Button
                      variant="outline-primary"
                      className="w-100 mb-3"
                      onClick={forgotPassword}
                    >
                      Forgot Password
                    </Button>

                    <Link to="/customer-register">
                      <Button variant="outline-success" className="w-100 mb-3">
                        <FaUserPlus className="me-2" />
                        Create Password
                      </Button>
                    </Link>
                  </Form>
                )}

                {loginMode === "mobile" && (
                  <Alert variant="warning" className="text-center mb-3">
                    <FaClock className="me-2" />
                    <strong>Mobile OTP Login Coming Soon</strong>
                    <br />
                    <span>
                      Please use Email Login for now. Mobile OTP login will be
                      available soon.
                    </span>
                  </Alert>
                )}

                <Link to="/">
                  <Button variant="outline-dark" className="w-100">
                    <FaArrowLeft className="me-2" />
                    Back to Home
                  </Button>
                </Link>
              </Card.Body>
            </Card>

            <p className="text-center text-white mt-3 small">
              Login is available only for registered Trading Wealth customers.
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default CustomerLogin;